import React, { forwardRef, Ref } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import classNames from 'classnames';

import { ChevronDownIcon, ChevronUpIcon } from '../../assets';
import { SelectItem } from './SelectItem';

import styles from './Select.module.scss';

interface IOptionBase {
    value: string | number;
    label: string;
}

export interface ISelectProps<T extends boolean = false> extends Omit<RadixSelect.SelectProps, 'onValueChange'> {
    size?: 'small' | 'regular';
    placeholder?: string;
    error?: boolean;
    isNumber?: T;
    options: IOptionBase[];
    prefix?: React.ReactNode;
    className?: string;
    valueClassName?: string;
    style?: React.CSSProperties;
    onValueChange?: (value: T extends true ? number : string) => void;
}
type SelectComponent = <T extends boolean = false>(
    props: ISelectProps<T> & { ref?: React.Ref<HTMLButtonElement> },
) => React.ReactElement | null;

export const Select = forwardRef(function Select<T extends boolean = false>(
    {
        placeholder,
        error,
        options,
        prefix,
        size = 'regular',
        className,
        valueClassName,
        style,
        isNumber,
        onValueChange,
        value,
        ...rootProps
    }: ISelectProps<T>,
    ref: Ref<HTMLButtonElement>,
) {
    return (
        <RadixSelect.Root
            value={value?.toString()}
            onValueChange={(value) => {
                if (isNumber) {
                    onValueChange?.(+value as T extends true ? number : string);
                } else {
                    onValueChange?.(value as T extends true ? number : string);
                }
            }}
            {...rootProps}
        >
            <RadixSelect.Trigger
                ref={ref}
                className={classNames(
                    styles.triggerButton,
                    styles[`size-${size}`],
                    {
                        [styles.error]: error,
                        [styles.withPrefix]: !!prefix,
                    },
                    className,
                )}
                style={style}
            >
                {prefix && <div className={styles.startIcon}>{prefix}</div>}
                <RadixSelect.Value className={valueClassName} placeholder={placeholder} />
                <RadixSelect.Icon>
                    <ChevronDownIcon className={styles.triggerIcon} />
                </RadixSelect.Icon>
            </RadixSelect.Trigger>

            <RadixSelect.Portal>
                <RadixSelect.Content className={styles.content} position="item-aligned">
                    <RadixSelect.ScrollUpButton className={styles.scrollButtonUp}>
                        <ChevronUpIcon />
                    </RadixSelect.ScrollUpButton>
                    <RadixSelect.Viewport className={styles.viewport}>
                        {options.map(({ label, value }) => (
                            <SelectItem size={size} key={value} value={String(value)}>
                                {label}
                            </SelectItem>
                        ))}
                    </RadixSelect.Viewport>
                    <RadixSelect.ScrollDownButton className={styles.scrollButtonDown}>
                        <ChevronDownIcon />
                    </RadixSelect.ScrollDownButton>
                    <RadixSelect.Arrow />
                </RadixSelect.Content>
            </RadixSelect.Portal>
        </RadixSelect.Root>
    );
}) as SelectComponent;
